import { useState, useEffect, useContext } from 'react'
import { useMutation } from "@apollo/client";
import { useApolloClient } from "@apollo/client";
import { useDisclosure, Button, Image, useToast, Avatar } from "@chakra-ui/react";
import { uploadWithSasToken } from "../utils/helper";
import {
    CREATE_INSTRUCTOR,
    UPDATE_INSTRUCTOR,
} from "../graphql/mutations";
import {
    PUBLISHER_DATA
} from "../graphql/queries";
import { PublisherAndUserContext } from '../contexts/PublisherAndUserContext';

export const useInstructors = () => {
    const {user, publisher, updatePublisherState} = useContext(PublisherAndUserContext);
    const [staffs, setStaffs] = useState([]);
    const [instructor, setInstructor] = useState({});
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const { 
        isOpen: addInstructorIsOpen, 
        onOpen: addInstructorOnOpen, 
        onClose: addInstructorOnClose 
    } = useDisclosure();
    const {
        isOpen: deleteModalIsOpen,
        onOpen: deleteModalOnOpen,
        onClose: deleteModalOnClose,
    } = useDisclosure();
    const {
        isOpen: updateModalIsOpen,
        onOpen: updateModalOnOpen,
        onClose: updateModalOnClose,
    } = useDisclosure();
    const toast = useToast();
    const apolloClient = useApolloClient();

    const filterMatchingNames = (arr1, arr2) => {
        // Create a Set of InstructorName from arr2 for faster lookup
        const namesSet = new Set(arr2.map(item => item.InstructorName));

        // Filter arr1 by checking if the full name is not in the namesSet
        return arr1.filter(
            person => !namesSet.has(`${person.firstName} ${person.lastName}`)
        );
    }

    const [createInstructor] = useMutation(CREATE_INSTRUCTOR, {
        onCompleted: (data) => {
            setLoading(false);
            const exisiting = apolloClient.cache.readQuery({
                query: PUBLISHER_DATA, variables: {
                    where: {
                        email: user.email
                    }
                }
            })
            if (exisiting?.edShopifyPublisher) {
                apolloClient.cache.writeQuery({
                    query: PUBLISHER_DATA,
                    data: {
                        ...exisiting?.edShopifyPublisher,
                        instructors: [
                            ...exisiting?.edShopifyPublisher?.instructors,
                            data.edshopifyCreateInstructor
                        ]
                    }
                })
            }
            setInstructors([...instructors, data.edshopifyCreateInstructor]);
            updatePublisherState({
                ...publisher,
                instructors: [
                    ...publisher?.instructors,
                    data.edshopifyCreateInstructor
                ]
            })
            addInstructorOnClose();
            toast({
                position: "top-right",
                description: `${data.edshopifyCreateInstructor.InstructorName} added as Instructor`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
        error: () => {
            setLoading(false);
            toast({
                position: "top-right",
                description: `Error while creating instructor. Please try again.`,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        },
    });

    const [updateInstructor] = useMutation(UPDATE_INSTRUCTOR, {
        onCompleted: (data) => {
            setLoading(false);
            updateModalClose();
            const instructorsCopy = [...instructors];
            const index = instructorsCopy.findIndex(ins => ins.id === data?.updateInstructor?.id)
            if (index > -1) {
                instructorsCopy[index] = {
                    ...instructorsCopy[index],
                    ...data?.updateInstructor
                }
            }
            setInstructors(instructorsCopy);
            updatePublisherState({
                ...publisher,
                instructors: instructorsCopy
            })
        },
        error: () => {
            setLoading(false);
        },
    });

    const updateModalClose = () => {
        updateModalOnClose()
        setInstructor({})
    }

    const addInstructors = async (e) => {
        e.preventDefault();
        setLoading(true);
        let newInstructorCopy = {...instructor};
        if(!newInstructorCopy.InstructorName) {
            newInstructorCopy = {
                ...newInstructorCopy,
                InstructorName: list[0].name
            }
        }
        let src = "";
        if (newInstructorCopy.file) {
            const sasUrl = await uploadWithSasToken(publisher?.id, "images", newInstructorCopy.file);
            src = sasUrl.split("?")[0];
        }
        newInstructorCopy = {
            ...newInstructorCopy,
            InstructorPic: src,
            InstructorBio: newInstructorCopy.InstructorBio
        };
        delete newInstructorCopy.id
        delete newInstructorCopy.file
        createInstructor({
            variables: {
                where: {
                    id: publisher?.id
                },
                data: {
                    ...newInstructorCopy,
                    publisher: {
                        connect: {
                            id: publisher?.id
                        }
                    }
                }
            },
        });
    };

    const onChange = (e, type) => {
        if(type === "image") {
            setInstructor({...instructor, file: e});
        } else {
            setInstructor({...instructor, [e.target.name]: e.target.value})
        }
    };

    const onModalClose = () => {
        addInstructorOnClose();
    };

    const updateCallback = (item) => {
        setInstructor(item);
        updateModalOnOpen();
    };

    const updateInstructorCallback = async () => {
        setLoading(true);
        let instructorSrc = "";
        if (instructor.file) {
            const sasUrl = await uploadWithSasToken(publisher?.id, "images", instructor.file);
            instructorSrc = sasUrl.split("?")[0];
        }
        updateInstructor({
            variables: {
                where: {
                    id: instructor.id,
                },
                data: {
                    InstructorName: { set: instructor.InstructorName },
                    InstructorBio: { set: instructor.InstructorBio },
                    ...(instructorSrc ? { InstructorPic: { set: instructorSrc } } : {}),
                },
            },
        });
    };
    const openAddInstructor = () => {
        const filteredStaffs = filterMatchingNames(staffs, instructors)
        setStaffs(filteredStaffs)
        addInstructorOnOpen()
    }

    const deleteResource = () => {
        // deleteSelectedResource({
        //   variables: {
        //     where: {
        //       id: instructor.id,
        //     },
        //   },
        // });
    };

    const instructorsColumn = [
        { Header: "Name", accessor: "InstructorName" },
        { Header: "Bio", accessor: "InstructorBio" },
        {
            Header: "Picture", accessor: "InstructorPic",
            Cell: ({ cell }) => {
                return (
                    <>
                        { 
                            cell?.row?.original.InstructorPic ? 
                            <Image src={cell?.row?.original.InstructorPic} sx={{ width: "120px" }} alt={cell?.row?.original.InstructorName} /> : 
                            <Avatar name={cell?.row?.original.InstructorName} src='https://bit.ly/broken-link' borderRadius={0}/>
                        }
                    </>
                );
            },
        },
        {
            Header: "Update",
            Cell: ({ cell }) => {
                return (
                    <Button
                        variant={"ghost"}
                        colorScheme="blue"
                        _hover={{ bg: "trasnparent", color: "blue.80" }}
                        padding="0"
                        onClick={() => updateCallback(cell?.row?.original)}
                    >
                        Update
                    </Button>
                );
            },
        }
    ]

    useEffect(() => {
        if((publisher?.id && publisher?.additionalAdmins?.length > 0) || (publisher?.id && publisher?.staff?.length > 0)) {
            setStaffs([...publisher?.additionalAdmins?.map(user => ({ ...user, role: "Co-Admin" })), ...publisher?.staff?.map(user => ({ ...user, role: "Staff" }))]);
        }
        if((publisher?.id && instructors?.length === 0 && publisher?.additionalAdmins?.length > 0) || (publisher?.id && instructors?.length === 0 && publisher?.staff?.length > 0)) {
            setInstructors(publisher?.instructors)
            setList([...publisher.staff, ...publisher.additionalAdmins, publisher.owner])
        }
        if((publisher?.id && instructors?.length > 0 && publisher?.additionalAdmins?.length > 0) || (publisher?.id && instructors?.length > 0 && publisher?.staff?.length > 0)) {
            const namesToRemove = new Set(instructors.map(i => i.InstructorName));
            setList([...publisher.staff, ...publisher.additionalAdmins, publisher.owner].map(n => ({...n, name: `${n.firstName} ${n.lastName}`})).filter(i => !namesToRemove.has(i.name)))
        }
    }, [publisher?.id, instructors, publisher.additionalAdmins, publisher.staff, publisher?.instructors, publisher.owner]);

    return {
        instructor,
        instructors,
        instructorsColumn,
        openAddInstructor,
        addInstructorIsOpen,
        addInstructorOnOpen,
        onModalClose,
        addInstructors,
        staffs,
        onChange,
        updateModalIsOpen,
        updateModalClose,
        updateInstructorCallback,
        deleteModalIsOpen,
        deleteModalOnClose,
        deleteResource,
        setInstructor,
        loading,
        publisher,
        list,
        deleteModalOnOpen,
        user,
    }
}
